import Card, { CardContent } from '@/components/card';
import { ExpandMoreButton } from '@/components/expand-more-button';
import TextStack, {
  TextLabel,
  TextStackItem,
  TextValue
} from '@/components/text-stack';
import { DisbursementRequestAlertDto } from '@/models/suba/alerts/disbursement-request-alert-dto.model';
import { useAlertContext } from '@/routes/suba/common/contexts/AlertContext';
import { DisbursementService } from '@/services/suba/disbursement/Disbursement.service';
import formatters from '@/utils/Formatters';
import { RemoveRedEyeOutlined, VisibilityOff } from '@mui/icons-material';
import {
  Collapse,
  Divider,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useToggle } from '@vestwell-frontend/hooks';

import { FC } from 'react';

import { CardHeader } from '../../../common/CardHeader.component';

export const DisbursementDestinations: FC = () => {
  const alert = useAlertContext<DisbursementRequestAlertDto>();

  const [isBankAccountNumberShown, toggleIsBankAccountNumberShown] =
    useToggle(false);
  const [isExpanded, toggleIsExpanded] = useToggle(true);

  const getDisbursementQuery = useQuery(
    ['DisbursementService.getById', alert?.details?.disbursementRequestQueueId],
    () => DisbursementService.getById(alert.details.disbursementRequestQueueId),
    {
      enabled: !!alert?.details?.disbursementRequestQueueId
    }
  );

  return (
    <Card size='small'>
      <CardHeader
        action={
          <ExpandMoreButton
            aria-label='expand details'
            data-testid='destinations-expand-more-button'
            expand={isExpanded}
            onClick={toggleIsExpanded}
          />
        }
        title='Destinations'
      />
      <Divider />
      <Collapse in={isExpanded}>
        <CardContent>
          <Stack flex={1} spacing={1}>
            <Typography variant='subtitle2'>General</Typography>
            <TextStack direction='column'>
              <TextStackItem>
                <TextLabel>Delivery Method</TextLabel>
                <TextValue>
                  {formatters.displayCase(
                    getDisbursementQuery.data?.disbursementMethod
                  )}
                </TextValue>
              </TextStackItem>
              <TextStackItem>
                <TextLabel>Requested Amount</TextLabel>
                <TextValue>
                  {formatters.formatDollars(getDisbursementQuery.data?.amount)}
                </TextValue>
              </TextStackItem>
              <TextStackItem>
                <TextLabel>Bank Name</TextLabel>
                <TextValue>
                  {formatters.displayCase(
                    getDisbursementQuery.data?.receivingBankName
                  )}
                </TextValue>
              </TextStackItem>
              <TextStackItem>
                <TextLabel>Routing Number</TextLabel>
                <TextValue>
                  {getDisbursementQuery.data?.receivingBankRoutingNumber}
                </TextValue>
              </TextStackItem>
              <TextStackItem>
                <TextLabel>Account Number</TextLabel>
                <TextValue
                  icon={
                    <IconButton
                      edge='start'
                      onClick={toggleIsBankAccountNumberShown}
                      size='small'>
                      {isBankAccountNumberShown ? (
                        <RemoveRedEyeOutlined fontSize='inherit' />
                      ) : (
                        <VisibilityOff fontSize='inherit' />
                      )}
                    </IconButton>
                  }
                  iconAlign='right'>
                  {getDisbursementQuery.data?.receivingBankAccountNumber
                    ? isBankAccountNumberShown
                      ? getDisbursementQuery.data.receivingBankAccountNumber
                      : formatters.maskBankNumber(
                          getDisbursementQuery.data.receivingBankAccountNumber
                        )
                    : ''}
                </TextValue>
              </TextStackItem>
            </TextStack>
          </Stack>
        </CardContent>
      </Collapse>
    </Card>
  );
};

DisbursementDestinations.displayName = 'DisbursementDestinations';
